import * as React from 'react'
import clsx from 'clsx'
import { SwitchBase, SwitchBaseProps } from '../SwitchBase'
import { SxProps, sx as sxFunc } from '../sx'
import CheckBoxUncheckedIcon from '../internal/svg-icons/CheckBoxUnchecked'
import CheckBoxIcon from '../internal/svg-icons/CheckBoxChecked'
import CheckBoxIndeterminateIcon from '../internal/svg-icons/CheckBoxIndeterminate'
import style from './index.module.scss'

export interface CheckboxClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if `checked={true}`. */
  checked: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** State class applied to the root element if `indeterminate={true}`. */
  indeterminate: string
}

export interface CheckboxProps extends SwitchBaseProps {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<CheckboxClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * If `true`, the component appears indeterminate.
   * This does not set the native input element to indeterminate due
   * to inconsistent behavior across browsers.
   * However, we set a `data-indeterminate` attribute on the `input`.
   * @default false
   */
  indeterminate?: boolean
  /**
   * The icon to display when the component is indeterminate.
   */
  indeterminateIcon?: JSX.Element
  /**
   * The size of the component.
   * `small` is equivalent to the dense checkbox styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

const defaultCheckedIcon = <CheckBoxIcon />
const defaultIcon = <CheckBoxUncheckedIcon />
const defaultIndeterminateIcon = <CheckBoxIndeterminateIcon />

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  function Checkbox(props, ref) {
    const {
      checkedIcon = defaultCheckedIcon,
      color = 'primary',
      icon: iconProp = defaultIcon,
      indeterminate = false,
      indeterminateIcon: indeterminateIconProp = defaultIndeterminateIcon,
      inputProps,
      size = 'medium',
      className,
      classes,
      sx,
      ...other
    } = props

    const icon = indeterminate ? indeterminateIconProp : iconProp
    const indeterminateIcon = indeterminate
      ? indeterminateIconProp
      : checkedIcon

    const _classes = {
      root: clsx([
        indeterminate && [
          style[`indeterminate--${color}`],
          classes?.indeterminate,
        ],
        style[`color--${color}`],
        classes?.root,
        sxFunc(sx),
      ]),
      disabled: clsx(style['disabled'], classes?.disabled),
      checked: clsx(style[`checked--${color}`], classes?.checked),
    }
    const iconSize = {
      small: 16,
      medium: 20,
      large: 24,
    } as const
    return (
      <SwitchBase
        type="checkbox"
        classes={_classes}
        className={clsx(
          style['root'],
          classes?.root,
          style[`size--${size}`],
          className,
          sxFunc(sx)
        )}
        ref={ref}
        inputProps={{
          // @ts-expect-error
          'data-indeterminate': indeterminate,
          ...inputProps,
        }}
        // @ts-expect-error
        icon={React.cloneElement(icon, {
          // @ts-expect-error
          fontSize: icon?.props?.fontSize ?? iconSize[size],
        })}
        // @ts-expect-error
        checkedIcon={React.cloneElement(indeterminateIcon, {
          // @ts-expect-error
          fontSize: indeterminateIcon?.props?.fontSize ?? iconSize[size],
        })}
        {...other}
      />
    )
  }
)
